import React from 'react';
import { Modal, Avatar, Stack, Group, Badge, Text } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { formatDateTime } from '../../utils/date';
import { useDevice } from '../../hooks/useDevice';
import { ReviewRequest, User } from '../../types';
import {
  getReviewProgress,
  getReviewerVotes,
  hasRequiredReviewers,
  ReviewerVoteState,
} from '../../utils/reviewProgress';

interface CompetencyReviewDetailsModalProps {
  opened: boolean;
  onClose: () => void;
  selectedRequest: ReviewRequest | null;
  getStatusConfig: (status: string) => { color: string; text: string };
}

export const CompetencyReviewDetailsModal: React.FC<CompetencyReviewDetailsModalProps> = ({
  opened,
  onClose,
  selectedRequest,
  getStatusConfig,
}) => {
  const { t } = useTranslation();
  const deviceType = useDevice();

  if (!selectedRequest) return null;

  const voteColor: Record<ReviewerVoteState, string> = {
    approved: 'teal',
    rejected: 'red',
    awaiting: 'gray',
  };
  const votes = getReviewerVotes(selectedRequest);
  const { approved, total } = getReviewProgress(selectedRequest);

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={t('reviewRequests.details')}
      size={deviceType === 'mobile' ? '100%' : 'lg'}
      fullScreen={deviceType === 'mobile'}
    >
      <Stack gap="md">
        <Group justify="space-between">
          <Badge color={getStatusConfig(selectedRequest.status).color} size="lg">
            {getStatusConfig(selectedRequest.status).text}
          </Badge>
          <Text size="sm" c="dimmed">
            {formatDateTime(selectedRequest.createdAt)}
          </Text>
        </Group>

        <Stack gap="sm">
          <Text size="sm" c="dimmed">
            {t('reviewRequests.applicant')}
          </Text>
          <Group gap="sm">
            <Avatar src={selectedRequest.applicant.avatar} radius="xl" size="md" />
            <div>
              <Text fw={500} size="sm">
                {selectedRequest.applicant.name}
              </Text>
              <Text size="xs" c="dimmed">
                {selectedRequest.applicant.email}
              </Text>
            </div>
          </Group>
        </Stack>

        <Stack gap="sm">
          <Text size="sm" c="dimmed">
            {t('reviewRequests.reviewer')}
          </Text>
          {selectedRequest.reviewer ? (
            <Group gap="sm">
              <Avatar src={selectedRequest.reviewer.avatar} radius="xl" size="md" />
              <div>
                <Text fw={500} size="sm">
                  {selectedRequest.reviewer.name}
                </Text>
                <Text size="xs" c="dimmed">
                  {selectedRequest.reviewer.email}
                </Text>
              </div>
            </Group>
          ) : (
            <Text size="sm" c="dimmed">
              {t('reviewRequests.pooledReviewers', {
                count: selectedRequest.reviewerPool?.length || 0,
              })}
            </Text>
          )}
        </Stack>

        {hasRequiredReviewers(selectedRequest) && (
          <Stack gap="sm">
            <Group justify="space-between">
              <Text size="sm" c="dimmed">
                {t('reviewRequests.requiredReviewers')}
              </Text>
              <Badge variant="light" color="blue">
                {t('reviewRequests.approvalProgress', { approved, total })}
              </Badge>
            </Group>
            {votes.map((vote) => {
              const reviewer = typeof vote.user === 'string' ? null : (vote.user as User);
              return (
                <Group key={typeof vote.user === 'string' ? vote.user : reviewer?._id} gap="sm">
                  <Avatar src={reviewer?.avatar} radius="xl" size="sm" />
                  <div style={{ flex: 1 }}>
                    <Text size="sm" fw={500}>
                      {reviewer?.name || t('reviewRequests.reviewer')}
                    </Text>
                    {vote.comment && (
                      <Text size="xs" c="dimmed">
                        {vote.comment}
                      </Text>
                    )}
                  </div>
                  <Badge color={voteColor[vote.state]} variant="light">
                    {t(`reviewRequests.vote.${vote.state}`)}
                  </Badge>
                </Group>
              );
            })}
          </Stack>
        )}

        <Stack gap="sm">
          <Text size="sm" c="dimmed">
            {t('reviewRequests.competency')}
          </Text>
          <Text>{selectedRequest.competency.title}</Text>
          {selectedRequest.competency.description && (
            <Text size="xs" c="dimmed">
              {selectedRequest.competency.description}
            </Text>
          )}
        </Stack>

        <Stack gap="sm">
          <Text size="sm" c="dimmed">
            {t('reviewRequests.competencyMap')}
          </Text>
          <Text>{selectedRequest.competencyMap.name}</Text>
        </Stack>

        <Stack gap="sm">
          <Text size="sm" c="dimmed">
            {t('reviewRequests.comment')}
          </Text>
          <Text>{selectedRequest.comment}</Text>
        </Stack>

        {selectedRequest.reviewerComment && (
          <Stack gap="sm">
            <Text size="sm" c="dimmed">
              {t('reviewRequests.reviewerComment')}
            </Text>
            <Text>{selectedRequest.reviewerComment}</Text>
          </Stack>
        )}

        {selectedRequest.statusChangedAt && (
          <Stack gap="sm">
            <Text size="sm" c="dimmed">
              {t('reviewRequests.statusChangedAt')}
            </Text>
            <Text>{formatDateTime(selectedRequest.statusChangedAt)}</Text>
          </Stack>
        )}
      </Stack>
    </Modal>
  );
};
